import { getModel } from '../../model/registry'
import { useCallback, useState, useRef } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import useFormModal from '../modal/useModalDialog'
import pause from '../../utils/pause'
import invalidateListQueries from '../../utils/invalidateListQueries'
import { useMessage } from '../pageView/Page'
import { toGuardMessage } from '../../utils/guardMessage'

const _log = false

const useListBulkActions = (config, endpoint, setModalType, onRefreshAggregate) => {
  const { setShowModal, setModalConfig } = useFormModal()
  const { deleteMe, postMe } = useApi()
  const [, setMessage] = useMessage((s) => s)
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState(null)

  const pendingBulkRef = useRef(null)

  // Bulk delete function using batch endpoint
  const executeBulkDelete = async (items) => {
    try {
      // Use deleteMe with body parameter for batch delete
      const response = await deleteMe(`${endpoint}/batch`, null, {
        items: items.map(item => item.id)
      })

      // Show results summary
      const { successful, failed, total } = response.summary || { successful: items.length, failed: 0, total: items.length }

      if (failed > 0) {
        // The batch endpoint reports per-item guard rejections in `errors` while the
        // envelope still says success — surface the first reason so a silently
        // skipped row is not mistaken for a completed delete.
        console.log(`Bulk delete partial success: ${successful}/${total} items deleted`)
        const guardMessage = toGuardMessage(response)
        if (guardMessage) setMessage(guardMessage)
      } else {
        // Show complete success message
        console.log(`Bulk delete success: ${total} items deleted`)
        setMessage({ show: false })
      }

      // Refresh list (match new query key prefix)
      await invalidateListQueries(endpoint, queryClient)

      return response
    } catch (error) {
      console.error('Bulk delete error:', error.message)
      throw error
    }
  }

  // Bulk update function using batch endpoint
  const executeBulkUpdate = async (items, action, payload) => {
    try {
      //Preparing the Raw Data to be sent to server
      const rawData = { ...payload.data }
      delete rawData.defaulted
      delete rawData.message
      delete rawData.title

      let body = { items: [] }
      //adding all selected items to the body
      for (const item of items) {
        let content = { id: item.id }
        content = { ...content, ...rawData }
        body.items.push(content)
      }

      // Use postMe with body parameter for batch delete
      const response = await postMe(`${endpoint}/batch`, body)
      // Show results summary
      const { successful, failed, total } = response.summary || { successful: items.length, failed: 0, total: items.length }

      /*
       * The count is reported by the server, not from here: the batch route
       * broadcasts `batch.update` / `batch.updatePartial` when it finishes, and
       * serverEventData turns that into a toast.
       *
       * Not because the numbers are unavailable — they are right there in
       * `summary` — but because this hook cannot show them. A list route mounts
       * ListView straight from the router, with no MessageProvider above it, so
       * `setMessage` resolves to formContext's null store and silently does
       * nothing. The SSE toast renders off a Valtio proxy and needs no provider,
       * so it works on every route.
       */
      console.log(`Bulk update: ${successful}/${total} items updated, ${failed} failed`)

      // Refresh list (match new query key prefix)
      await invalidateListQueries(endpoint, queryClient)

      return response

    } catch (error) {
      console.error('Bulk update error:', error.message)
      throw error
    }


  }

  // Generic bulk action function for non-delete operations
  const executeBulkAction = async (action, items, payload = {}) => {
    // Validate maximum batch size
    if (items.length > 100) {
      throw new Error('Maximum 100 items can be selected for bulk operations')
    }

    // Handle delete action with batch endpoint
    if (action.action === 'delete') {
      return await executeBulkDelete(items)
    }

    // Handle update action with batch endpoint
    if (action.action === 'update') {
      return await executeBulkUpdate(items, action, payload)
    }

    // Handle other actions individually
    const results = []
    const errors = []

    // Show progress indicator
    setProgress({ current: 0, total: items.length })

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      try {
        const result = await postMe(`${endpoint}/${item.id}/${action.action}`, item)
        results.push({ item, result, success: true })
      } catch (error) {
        errors.push({ item, error: error.message, success: false })
      }

      setProgress({ current: i + 1, total: items.length })

      // Small delay to prevent overwhelming the server
      if (i < items.length - 1) {
        await pause(50)
      }
    }

    // Clear progress
    setProgress(null)

    // Refresh list data (match new query key prefix)
    await queryClient.invalidateQueries({ queryKey: ['list', endpoint] })

    return {
      results,
      errors,
      summary: {
        total: items.length,
        successful: results.length,
        failed: errors.length
      }
    }
  }

  // Form mutation for handling bulk actions
  const bulkActionMutation = useMutation({
    mutationFn: ({ action, items, payload }) => executeBulkAction(action, items, payload)
  })

  // Confirm function for bulk operations
  const runBulkConfirm = async (action, selectedCount) => {
    _log && console.log('🔵 runBulkConfirm called with action:', action, 'count:', selectedCount)

    // Create a modified modal config for bulk operations
    const bulkModalConfig = {
      ...getModel('modalDialogData')[action.modalConfig],
      title: t('bulk.confirm.title', { count: selectedCount }),
      message: t('bulk.confirm.message', {
        action: t(action.label).toLowerCase(),
        count: selectedCount
      })
    }

    setModalConfig(bulkModalConfig)
    // Set modal type to 'bulk' for bulk actions
    if (setModalType) {
      setModalType('bulk')
    }
    setShowModal(true)
    _log && console.log('🔵 Bulk confirmation modal should now be shown')
  }

  // Main handler function for bulk actions
  const handleBulkAction = useCallback(async (action, selectedItems) => {
    _log && console.log('🟢 handleBulkAction called with:', { action, selectedCount: selectedItems.length })


    // Validate selection count
    if (selectedItems.length === 0) {
      _log && console.log('🟡 No items selected')
      return
    }

    if (selectedItems.length > 100) {
      _log && console.log('🔴 Too many items selected:', selectedItems.length)
      // Show error message
      return
    }

    let response

    // Handle confirmation flow
    if (action.confirm) {
      _log && console.log('🟡 Confirmation required for bulk action')
      if (action.modalConfig) {
        _log && console.log('🟡 Using modal confirmation for bulk action')
        // Store the pending action context for modal callback
        pendingBulkRef.current = { items: selectedItems, action }
        // This will be handled by the modal callback system
        runBulkConfirm(action, selectedItems.length)
        return
      } else {
        _log && console.log('🟡 Using window.confirm fallback for bulk action')
        // Fallback to window.confirm if no modalConfig
        const confirmed = window.confirm(
          t('bulk.confirm.fallback', {
            action: t(action.label).toLowerCase(),
            count: selectedItems.length
          }) || `Are you sure you want to ${action.action} ${selectedItems.length} items?`
        )
        if (!confirmed) return
      }
    }

    _log && console.log('🟢 Executing bulk action directly')
    // Execute the bulk action
    response = await bulkActionMutation.mutateAsync({ action, items: selectedItems })
    if (action?.refreshAggregate && onRefreshAggregate) onRefreshAggregate()
    return response
  }, [bulkActionMutation, t])

  // Modal callback handler for bulk operations
  const handleBulkModalAction = useCallback(async (actionData, selectedItems) => {
    _log && console.log('🔴 handleBulkModalAction called with:', actionData)
    const { action, payload } = actionData

    // Close the modal first
    setShowModal(false)
    _log && console.log('🔴 Modal closed')

    const itemsFromRef = pendingBulkRef.current?.items
    const itemsToActOn = selectedItems && selectedItems.length > 0 ? selectedItems : itemsFromRef

    // Execute the bulk action if confirmed
    if (action !== 'close' && itemsToActOn && itemsToActOn.length > 0) {
      _log && console.log('🔴 Executing bulk action for action:', action)

      // Create a confirmed action object
      const confirmedAction = { action, confirm: false } // Remove confirmation since already confirmed

      const confirmedOriginalAction = pendingBulkRef.current?.action
      await bulkActionMutation.mutateAsync({ action: confirmedAction, items: itemsToActOn, payload: payload })
      if (confirmedOriginalAction?.refreshAggregate && onRefreshAggregate) onRefreshAggregate()
      _log && console.log('🔴 Bulk action completed')
      // Clear pending ref after handling
      pendingBulkRef.current = null
    } else {
      _log && console.log('🔴 Bulk action cancelled or no items selected')
      // Clear pending ref on cancel/close as well
      pendingBulkRef.current = null
    }
  }, [bulkActionMutation, setShowModal, pendingBulkRef])

  return {
    handleBulkAction,
    handleBulkModalAction,
    isLoading: bulkActionMutation.isPending,
    progress
  }
}

export default useListBulkActions
